/**
 * countryCodes.ts
 *
 * Fuente de verdad única de códigos de país para toda la aplicación.
 * Importar siempre desde aquí — nunca definir localmente en componentes.
 *
 * CÓMO AGREGAR UN PAÍS:
 * 1. Agregar entrada al array COUNTRY_CODES
 * 2. El cambio aplica automáticamente a todos los componentes que importan de aquí
 *
 * Componentes que consumen este archivo:
 *   - ProfilePage.tsx          → usa formato CountryCode (code, flag, digits, format)
 *   - CreateLeadModal.tsx      → usa formato CountryCodeOption (value, label, digits)
 *   - LeadDetailModal.tsx      → usa formato CountryCodeOption (value, label)
 */

// ── Tipo base ──────────────────────────────────────────────────────────────
export interface CountryCode {
  code: string      // '+52'
  country: string   // 'México'
  flag: string      // '🇲🇽'
  digits: number    // 10
  format: string    // 'XX XXXX XXXX'
}

// ── Tipo compatible con componentes que usan value/label ───────────────────
export interface CountryCodeOption {
  value: string     // '+52'
  label: string     // '🇲🇽 +52'
  country: string   // 'México'
  digits: number    // 10
}

// ── Array principal ────────────────────────────────────────────────────────
export const COUNTRY_CODES: CountryCode[] = [
  { code: '+52',  country: 'México',      flag: '🇲🇽', digits: 10, format: 'XX XXXX XXXX'  },
  { code: '+1',   country: 'USA/Canadá',  flag: '🇺🇸', digits: 10, format: 'XXX XXX XXXX'  },
  { code: '+34',  country: 'España',      flag: '🇪🇸', digits: 9,  format: 'XXX XX XX XX'  },
  { code: '+54',  country: 'Argentina',   flag: '🇦🇷', digits: 10, format: 'XX XXXX XXXX'  },
  { code: '+57',  country: 'Colombia',    flag: '🇨🇴', digits: 10, format: 'XXX XXX XXXX'  },
  { code: '+56',  country: 'Chile',       flag: '🇨🇱', digits: 9,  format: 'X XXXX XXXX'   },
  { code: '+51',  country: 'Perú',        flag: '🇵🇪', digits: 9,  format: 'XXX XXX XXX'   },
  { code: '+507', country: 'Panamá',      flag: '🇵🇦', digits: 8,  format: 'XXXX XXXX'     },
  { code: '+506', country: 'Costa Rica',  flag: '🇨🇷', digits: 8,  format: 'XXXX XXXX'     },
  { code: '+504', country: 'Honduras',    flag: '🇭🇳', digits: 8,  format: 'XXXX XXXX'     },
  { code: '+502', country: 'Guatemala',   flag: '🇬🇹', digits: 8,  format: 'XXXX XXXX'     },
  { code: '+503', country: 'El Salvador', flag: '🇸🇻', digits: 8,  format: 'XXXX XXXX'     },
  { code: '+593', country: 'Ecuador',     flag: '🇪🇨', digits: 9,  format: 'XXX XXX XXX'   },
]

// ── Formato alternativo para selects con value/label ──────────────────────
// Usado por CreateLeadModal y LeadDetailModal
export const COUNTRY_CODE_OPTIONS: CountryCodeOption[] = COUNTRY_CODES.map(c => ({
  value:   c.code,
  label:   `${c.flag} ${c.code}`,
  country: c.country,
  digits:  c.digits,
}))

// ── Helpers ────────────────────────────────────────────────────────────────

/** Obtener país por código. Fallback a México si no se encuentra. */
export const getCountryByCode = (code: string): CountryCode =>
  COUNTRY_CODES.find(c => c.code === code) || COUNTRY_CODES[0]

/** Código por defecto */
export const DEFAULT_COUNTRY_CODE = '+52'
